import { EBrandedIconShapeColor } from '@spinwise/react-component-library/BrandedIcon';
import {
  IApplicationState,
  postBonusRandom,
  postCancelBonus,
  setWheelNotificationBonus,
} from '@spinwise/spinwise-core';

import {
  getOverlayBonusInfo,
  getOverlayBonusOverride,
  getOverlayHasLuckyWheelBonus,
} from 'Components/_brand/overlays/brandOverlaysLibrary';
import { t } from 'i18next';
import { PATHS } from 'Libraries/routes.library';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { basicChannel } from 'src/channels/basic.channel';
import { EBrandIconIconPathShared, EBrandIconStatusPathShared } from 'src/libraryShared/icons';
import { amountToCurrency } from 'Utils/index';
import useWheelNotificationBonus from './useWheelNotificationBonus';
import { EWheelBonusOverlays, EWheelBonusOverlayStorage, EWheelBonusPopupType } from './WheelBonusIframe';

export type TWheelStatus = 'has-deposit-bonus' | 'has-notification' | 'ready-to-spin';

const WheelBonusPopupManager = () => {
  const dispatch = useDispatch();
  const balance = useSelector((state: IApplicationState) => state.accountReducer.info?.balance);
  const activeBonus = useSelector((state: IApplicationState) => state.accountReducer.info?.activeBonus);
  const wheelNotificationBonus = useWheelNotificationBonus();

  const router = useRouter();

  const closePopup = () => {
    basicChannel.send({
      action: 'close',
    });
    // item is not necessary anymore
    localStorage.removeItem(EWheelBonusOverlayStorage.POPUP_NAME);
    localStorage.removeItem(EWheelBonusOverlayStorage.POPUP_TYPE);
  };

  const openBonusOverlay = async ({ amount, bonusName, game, bonusEid, free_rounds }: EWheelBonusPopupType) => {
    postBonusRandom(
      bonusEid,
      'activate',
    )(dispatch).then(() => {
      getOverlayBonusInfo({
        title: t('wheel.popup.bonus.activate.title'),
        description: t('wheel.popup.bonus.activate.description', {
          AMOUNT: amount,
          BONUS_NAME: bonusName,

          GAME_NAME: game ?? free_rounds?.game?.name ?? '',
          interpolation: {
            escapeValue: false,
          },
        }),

        icon: {
          shapeColor: EBrandedIconShapeColor.Success,
          statusPath: EBrandIconStatusPathShared.SUCCESS,
          iconPath: EBrandIconIconPathShared.Bonus,
        },
        button: {
          text: t('wheel.popup.bonus.activate.button'),
          onClickEvent: async () => {
            closePopup();

            if (!free_rounds) return;

            router.push(`/play/${free_rounds?.game?.urlKey}`);
          },
          theme: 'Primary',
        },
        onClose: () => {
          closePopup();
        },
      });
    });
  };
  const openBonusMoneyOverlay = async ({
    amount,
    bonusName,
    game,
    bonusEid,

    free_rounds,
    type,
  }: EWheelBonusPopupType) => {
    postBonusRandom(
      bonusEid,
      'activate',
    )(dispatch).then(() => {
      getOverlayBonusInfo({
        title: t(`wheel.popup.bonus.money.activate.title`),
        description: t(`wheel.popup.bonus.${type}.activate.description`, {
          AMOUNT: amount,
          BONUS_NAME: bonusName,
          GAME_NAME: game ?? free_rounds?.game?.name ?? '',
          interpolation: {
            escapeValue: false,
          },
        }),

        icon: {
          shapeColor: EBrandedIconShapeColor.Success,
          statusPath: EBrandIconStatusPathShared.SUCCESS,
          iconPath: EBrandIconIconPathShared.Bonus,
        },
        button: {
          text: t(`wheel.popup.bonus.money.activate.button`),
          onClickEvent: async () => {
            closePopup();
          },
          theme: 'Secondary',
        },
        onClose: () => {
          closePopup();
        },
      });
    });
  };
  const openLaterOverlay = (amount = '', bonusName = '', gameName = '', type = 'money') => {
    const isCoin = type === 'coin';

    getOverlayBonusInfo({
      title: isCoin ? t('wheel.popup.bonus.activate.coin.later.title') : t('wheel.popup.bonus.activate.later.title'),
      description: t(`wheel.popup.bonus.activate.${type}.later.description`, {
        AMOUNT: amount,
        BONUS_NAME: bonusName,
        GAME_NAME: gameName,
        interpolation: {
          escapeValue: false,
        },
      }),

      icon: {
        shapeColor: EBrandedIconShapeColor.Success,
        statusPath: EBrandIconStatusPathShared.SUCCESS,
        iconPath: EBrandIconIconPathShared.Bonus,
      },
      button: {
        text: t('wheel.popup.bonus.activate.later.button'),
        onClickEvent: () => {
          closePopup();
          router.push(PATHS.BONUSES);
        },
        theme: 'Secondary',
      },
      onClose: () => {
        closePopup();
      },
    });
  };

  const getAmmountOfCurrentBonus = () => {
    if (parseFloat(balance?.bonus) > 0) return amountToCurrency(balance?.bonus, balance?.currency?.code);

    const free_rounds = activeBonus?.data?.free_rounds?.count;
    if (parseFloat(balance?.bonus) <= 0 && free_rounds > 0) return `${free_rounds}`;

    const in_game_bonus_value = activeBonus?.data?.free_rounds?.in_game_bonus_value;
    if (in_game_bonus_value && in_game_bonus_value > 0) {
      return amountToCurrency(in_game_bonus_value, balance?.currency?.code);
    }
    return '';
  };

  useEffect(() => {
    if (router.pathname != PATHS.LOBBY && router.pathname != PATHS.GAMES) return;
    const flag: EWheelBonusOverlays = localStorage.getItem(EWheelBonusOverlayStorage.POPUP_NAME) as EWheelBonusOverlays;
    const bonusInfo: EWheelBonusPopupType = JSON.parse(localStorage.getItem(EWheelBonusOverlayStorage.POPUP_TYPE));
    if (wheelNotificationBonus?.show == true && (!flag || !bonusInfo)) {
      getOverlayHasLuckyWheelBonus({
        onClose: () => {
          setWheelNotificationBonus({
            show: false,
            notification: null,
            allNotifications: wheelNotificationBonus?.allNotifications,
          })(dispatch);

          basicChannel.send({ action: 'close' });
        },
      });
    }
  }, [wheelNotificationBonus]);

  useEffect(() => {
    if (router.pathname != PATHS.LOBBY && router.pathname != PATHS.GAMES) return;
    const flag: EWheelBonusOverlays = localStorage.getItem(EWheelBonusOverlayStorage.POPUP_NAME) as EWheelBonusOverlays;
    const bonusInfo: EWheelBonusPopupType = JSON.parse(localStorage.getItem(EWheelBonusOverlayStorage.POPUP_TYPE));

    switch (flag) {
      case EWheelBonusOverlays.ACTIVATE_BONUS:
        if (!bonusInfo) {
          localStorage.removeItem(EWheelBonusOverlayStorage.POPUP_NAME);
          return;
        }

        if (bonusInfo.type === 'money') {
          openBonusMoneyOverlay(bonusInfo);
          return;
        }
        if (bonusInfo.type === 'jackpot') {
          openBonusMoneyOverlay(bonusInfo);
          return;
        }

        openBonusOverlay(bonusInfo);
        return;
      case EWheelBonusOverlays.WARNING_ACTIVATE_LATER:
        if (!bonusInfo) {
          localStorage.removeItem(EWheelBonusOverlayStorage.POPUP_NAME);
          return;
        }
        openLaterOverlay(bonusInfo.amount, bonusInfo.bonusName, bonusInfo.game, bonusInfo?.type);
        return;
      case EWheelBonusOverlays.WARNING_HAS_ACTIVE_BONUS:
        getOverlayBonusOverride({
          title: t('wheel.popup.warning.has.bonus.title'),
          description: t('wheel.popup.warning.has.bonus.description'),
          icon: {
            shapeColor: EBrandedIconShapeColor.Warning,
            statusPath: EBrandIconStatusPathShared.ATTENTION,
            iconPath: EBrandIconIconPathShared.Bonus,
          },
          buttonLeft: {
            text: t('wheel.popup.warning.has.bonus.btn.keep'),
            onClickEvent: () => {
              basicChannel.send({
                action: 'close',
              });
              if (!bonusInfo) {
                localStorage.removeItem(EWheelBonusOverlayStorage.POPUP_NAME);
                return;
              }

              openLaterOverlay(bonusInfo.amount, bonusInfo.bonusName, bonusInfo.game, bonusInfo?.type);
            },
          },
          buttonRight: {
            text: t('wheel.popup.warning.has.bonus.btn.change'),
            onClickEvent: () => {
              basicChannel.send({
                action: 'close',
              });
              if (!bonusInfo) {
                localStorage.removeItem(EWheelBonusOverlayStorage.POPUP_NAME);
                return;
              }
              postCancelBonus()(dispatch, {
                onAfterUpdate: () => {
                  if (bonusInfo.type === 'money') {
                    openBonusMoneyOverlay(bonusInfo);
                    return;
                  }
                  if (bonusInfo.type === 'jackpot') {
                    openBonusMoneyOverlay(bonusInfo);
                    return;
                  }

                  openBonusOverlay(bonusInfo);
                },
              });
            },
          },
          currentBonus: {
            title: t('wheel.popup.warning.has.bonus.loss.title'),
            amount: getAmmountOfCurrentBonus(),
          },
          onClose: () => {
            closePopup();
          },
        });
        return;
      default:
        break;
    }
    window.addEventListener('beforeunload', handleBeforeUnload);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  return null;
};
const handleBeforeUnload = () => {
  localStorage.removeItem(EWheelBonusOverlayStorage.POPUP_NAME);
};
export default WheelBonusPopupManager;
